import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import config from '../config'
import { Buffer } from "buffer";
import {login, setToken, logout} from "./sessionSlice"


export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: config.serverUrl,
        prepareHeaders: (headers, { getState }) => {
            if (!headers.get('authorization')) {
                const token = getState().session.token
                //we want to check if there is a valid token before injecting
                // otherwise accessing a public resource while not authenticated will create cors preflight issues
                if (token) {
                    headers.set('Authorization', `Bearer ${token}`)
                }
            }
            return headers
        }
    }),
    tagTypes: [
        'CompetitionEdition',
        'CompetitionRegistration',
        'UserServices',
        'EventPilotRegistrations',
        'EventResources',
        'CompetitionDay',
    ],
    endpoints: builder => ({
        authenticate: builder.mutation({
            query:(data) => ({
                url:'/token',
                method:'POST',
                headers:{
                    Authorization:'Basic '+Buffer.from(`${data.username}:${data.password}`).toString('base64')
                }
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    await dispatch(setToken(data))
                    await dispatch(apiSlice.endpoints.getPrincipal.initiate(null));
                } catch (error) {
                    console.log('**** Error on authentication')
                    console.log(error)
                }
            },
        }),
        /**called to refresh JWT token and user data when we have a token*/
        refreshAuth: builder.mutation({
            query:({token}) => ({
                url:'/token',
                method:'POST',
                headers:{
                    Authorization:'Bearer '+token
                }
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    await dispatch(setToken(data))
                    await dispatch(apiSlice.endpoints.getPrincipal.initiate(null));
                } catch (error) {
                    console.log('**** Token refresh Auth error')
                    console.log(error)
                    if (error.error.status === 401) {
                        //the session token is stale so we are logging out
                        //TODO: change this when we have a remember me token so that we can directly reauthenticate
                        dispatch(logout())
                        dispatch(apiSlice.util.resetApiState());
                        console.log('**** logging out')
                    }
                }
            },
        }),
        getPrincipal: builder.mutation({
            query : () => '/get/principal',
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    await dispatch(login(data))
                } catch (error) {
                    console.log('**** Error on getPrincipal')
                    console.log(error)
                }
            },
        }),
        preauth: builder.query({
            query:(data) => ({
                url: '/public/preauth',
                method:'POST',
                body:data,
            }),

        }),
        getRegions: builder.query({
            query: () => '/get/region'
        }),
        getFlights: builder.query({
            query: ({pilotId,page}) => `/get/flight?pilotId=${pilotId}&page=${page}`
        }),
        getFlight: builder.query({
            query: (id) => `/get/flight?id=${id}`
        }),
        getPerformance: builder.query({
            query: (id) => `/get/performance?id=${id}`
        }),
        getFlightPerformances: builder.query({
            query: (flightId) => `/get/performance?flightId=${flightId}`
        }),
        getFlightMetadata: builder.query({
            query: (flight) => `/load/flightMetadata?flightId=${flight.id}&userId=${flight.userId}`
        }),
        getGMap: builder.query({
            query: (flightId) => ({
                url: `/load/gmap?flightId=${flightId}`,
                responseHandler: (response) => response.text(),
            })
        }),
        get4D: builder.query({
            query: (flightId) => `/load/4d?flightId=${flightId}`
        }),
        get5D: builder.query({
            query: (flightId) => `/load/5d?flightId=${flightId}`
        }),
        getEvents: builder.query({
            query: () => `/public/get/events`,
            providesTags: ['CompetitionEdition'],
        }),
        getCompEvents: builder.query({ //this gets all events for admins
            query: ({compType}) => `/stadium/events/get?compType=${compType}`,
            providesTags: ['CompetitionEdition'],
        }),
        getCompEventList: builder.query({ //this gets all published events
            query: ({compType}) => `/stadium/events/list?compType=${compType}`,
            providesTags: ['CompetitionEdition'],
        }),
        getEvent: builder.query({
            query: (id) => `/public/get/event?id=${id}`,
            providesTags: ['CompetitionEdition'],
        }),
        getEventForRegistration: builder.query({
            query: (id) => `/public/get/event?id=${id}&registration=true`,
            providesTags: ['CompetitionEdition'],
        }),
        getIgcFileMeta: builder.query({
            query: (id) => `/get/igcFile?id=${id}`
        }),
        getIgcFileContent: builder.query({
            query: (id) => ({
                url: `/load/igcFile?id=${id}`,
                responseHandler: (response) => response.text(),
            }),
        }),
        getClubs: builder.query({
            query: (country) => `/public/get/clubs?country=${country}`
        }),
        register: builder.mutation({
            query:(data) => ({
                url:'/public/register',
                method:'POST',
                body:data,
            }),
        }),
        getUnpagedAirfields: builder.query({
            query:({id,country,startsWith}) => `/stadium/get/airfield?${id ? `id=${id}` : country ? `country=${country}` : startsWith ? `startsWith=${startsWith}` : ''}`
        }),
        createEvents: builder.mutation({
            query:(data) => ({
                url:'/stadium/events/create',
                method:'POST',
                body:data,
            }),
            invalidatesTags: ['CompetitionEdition'],
        }),
        getEventDefinition: builder.query({
            query:(id) => `/stadium/event/definition/get?id=${id}`,
            providesTags:['CompetitionEdition'],
        }),
        getEventManagerUser: builder.query({
            query:(id) => `/stadium/event/manager/get?id=${id}`,
            providesTags:['CompetitionEdition'],
        }),
        getPilotRegistrationPack: builder.query({
            query:(eventId) => `/stadium/event/registration/pack/get?eventId=${eventId}`,
            providesTags:['CompetitionRegistration'],
        }),
        isPilotRegisteredForEvent: builder.query({
            query:(eventId) => `/stadium/event/registration/isRegistered?eventId=${eventId}`,
            providesTags:['CompetitionRegistration'],
        }),
        listPilotRegistrations: builder.query({
            query:() => `/stadium/competitor/list/registrations?minStatus=1&maxStatus=1025`,
            providesTags:['CompetitionRegistration'],
        }),
        listPilotRegistrationDetails: builder.query({
            query:(id) => `/stadium/competitor/get/registrationDetails?id=${id}`,
            providesTags:['CompetitionRegistration'],
        }),
        updateCompetitionRegistration: builder.mutation({
           query: (data) => ({
               url:'/stadium/event/registration/update',
               method:'POST',
               body:data,
           }),
            invalidatesTags: ['CompetitionRegistration','EventPilotRegistrations'],
        }),
        updateCompetitionRegistrationDetails: builder.mutation({
           query: (data) => ({
               url:'/stadium/event/registration/details/update',
               method:'POST',
               body:data,
           }),
            invalidatesTags: ['CompetitionRegistration','EventPilotRegistrations'],
        }),
        withdrawFromEvent: builder.mutation({
           query: (eventId) => ({
               url:'/stadium/event/withdraw',
               method:'POST',
               body: {eventId:eventId},
           }),
            invalidatesTags: ['CompetitionRegistration','EventPilotRegistrations'],
        }),
        updateEventSetup: builder.mutation({
           query: (data) => ({
               url:'/stadium/event/setup/update',
               method:'POST',
               body:data,
           }),
            invalidatesTags: ['CompetitionEdition'],
        }),
        resetManagerPassword: builder.mutation({
            query: (eventId)=> ({
                url: `/stadium/events/resetManagerPassword?eventId=${eventId}`,
                method:'GET',
            })
        }),
        createCompetitionRegistration: builder.mutation({
            query:(data) => ({
                url:'/stadium/event/registration/create',
                method:'POST',
                body:data,
            }),
            invalidatesTags: ['CompetitionRegistration'],
        }),
        createLoginLink: builder.mutation({
            query:(data) => ({
                url:'/public/create/loginLink',
                method:'POST',
                body:data,
            }),
        }),
        createResetPasswordLink: builder.mutation({
            query:(data) => ({
                url:'/public/create/passwordResetLink',
                method:'POST',
                body:data,
            }),
        }),
        processRuid: builder.mutation({
            query:(ruid) => ({
                url:`/public/ruid/${ruid}`,
                method:'GET',
            }),
        }),
        resetPassword: builder.mutation({
            query:(data) => ({
                url:'/resetPassword',
                method:'POST',
                body:data,
            }),
        }),
        getUserEventServices: builder.query({
            query:() =>  `/stadium/user/services/sgp`,
            providesTags:['UserServices'],
        }),
        getEventPilotRegistrations: builder.query({
            query:({eventId}) =>  `/stadium/event/pilot/registrations?eventId=${eventId}`,
            providesTags:['EventPilotRegistrations'],
        }),
        getEventPilots: builder.query({
            query: (eventId) => `/public/get/event/registrations?eventId=${eventId}`,
            providesTags:['EventPilotRegistrations'],
        }),
        getCalendarEvents: builder.query({
            query: () => `/public/get/calendarEvents`,
            providesTags:['CompetitionEdition'],
        }),
        getEventResources:builder.query({
            query: ({eventId,dayId})=>`/stadium/event/resource/list?eventId=${eventId}${dayId ? '&dayId='+dayId : ''}`,
            providesTags:['EventResources'],
        }),
        getDays: builder.query({
            query: (eventId) => `/sgp/day/list?eventId=${eventId}`,
            providesTags:['CompetitionDay'],
        }),
        getDay: builder.query({
            query: (dayId) => `/sgp/day/load?dayId=${dayId}`,
            providesTags:['CompetitionDay'],
        }),
        createScoring: builder.mutation({
            query:(data) => ({
                url:'/sgp/scoring/create',
                method:'POST',
                body:data,
            }),
            invalidatesTags:[
                'CompetitionEdition',
                'CompetitionRegistration',
                'EventPilotRegistrations',
                'CompetitionDay'
            ],
        }),
        resetScoring: builder.mutation({
            query:(data) => ({
                url:'/sgp/scoring/reset',
                method:'POST',
                body:data,
            }),
            invalidatesTags:[
                'CompetitionEdition',
                'CompetitionRegistration',
                'EventPilotRegistrations',
                'CompetitionDay'
            ],
        }),
    })
})

export const {
    useGetRegionsQuery,
    useGetFlightsQuery,
    useGetFlightQuery,
    useGetPerformanceQuery,
    useGetFlightPerformancesQuery,
    useGetFlightMetadataQuery,
    useGetGMapQuery,
    useGet4DQuery,
    useGet5DQuery,
    useAuthenticateMutation,
    useRefreshAuthMutation,
    useGetPrincipalMutation,
    useGetEventsQuery,
    useGetCompEventsQuery,
    useGetCompEventListQuery,
    useGetEventQuery,
    useLazyGetEventQuery,
    useGetIgcFileMetaQuery,
    useGetIgcFileContentQuery,
    useLazyPreauthQuery,
    useLazyGetClubsQuery,
    useLazyGetUnpagedAirfieldsQuery,
    useGetUnpagedAirfieldsQuery,
    useRegisterMutation,
    useGetPilotRegistrationPackQuery,
    useCreateEventsMutation,
    useGetEventDefinitionQuery,
    useLazyGetEventDefinitionQuery,
    useGetEventManagerUserQuery,
    useGetEventForRegistrationQuery,
    useIsPilotRegisteredForEventQuery,
    useCreateCompetitionRegistrationMutation,
    useUpdateCompetitionRegistrationMutation,
    useUpdateCompetitionRegistrationDetailsMutation,
    useWithdrawFromEventMutation,
    useUpdateEventSetupMutation,
    useListPilotRegistrationsQuery,
    useListPilotRegistrationDetailsQuery,
    useCreateLoginLinkMutation,
    useProcessRuidMutation,
    useLazyGetUserEventServicesQuery,
    useGetEventPilotRegistrationsQuery,
    useLazyGetEventPilotRegistrationsQuery,
    useGetEventPilotsQuery,
    useLazyGetCalendarEventsQuery,
    useGetEventResourcesQuery,
    useResetPasswordMutation,
    useCreateResetPasswordLinkMutation,
    useGetDayQuery,
    useGetDaysQuery,
    useResetManagerPasswordMutation,
    useCreateScoringMutation,
    useResetScoringMutation,
} = apiSlice